/**
 * @file     index.tsx
 * @purpose  /properties map search shell: the split map and results panel
 *           (NeighbourhoodMapView) sized to the space under the navbar, seeded
 *           with the server-fetched cards, with the server-rendered PanelIntro
 *           (the page H1) at the top of the results panel and a visually hidden
 *           crawl nav of the seeded listings. Owns filter hydration, the
 *           preference sync and the shareable-URL sync. A Rent search keeps
 *           its own local memory and never writes the saved buy preference.
 */
"use client";

import { usePathname, useRouter } from "next/navigation";
import Box from "@mui/material/Box";
import Link from "next/link";
import dynamic from "next/dynamic";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { reltaTrack } from "@/hooks/useReltaTrack";
import { useAnalytics } from "@/hooks/useAnalytics";
import { searchGaParams, zeroResultsGaParams } from "@/services/analytics/gaEventParams";
import {
  DEFAULT_VALUE as DEFAULT_TRADITIONAL,
  type TraditionalSearchValue,
} from "@/components/search/searchValue";
import { getFilterState, setFilterState } from "@/components/search/filterMemory";
import { isRentSearchPath } from "@/components/search/searchModeSwitch";
import { featureFlags } from "@/core/config/featureFlags";
import { usePreferenceSync } from "@/hooks/usePreferenceSync";
import { useSupabaseUser } from "@/hooks/useSupabaseUser";
import { parseListingsResponse } from "./parseListingsResponse";
import { SuggestToClientProvider } from "@/features/properties/components/SuggestToClient";
import { writeNavContext } from "@/hooks/usePropertyNavContext";
import { filtersToSearchParams, filtersFromQuery } from "@/components/search/filterUrlCodec";
import { normalizeFilterValue } from "@/components/search/filterFieldRegistry";
import { queueAiSeedFromUrl } from "@/features/ai-chat/aiSeed";
import { replaceLocation, resolveSearchQuery } from "@/features/smart-search/useSmartSearch";
import { MapPaneFallback } from "@/views/neighbourhood-map/components/MapPaneFallback";
import { useAppTheme } from "@/core/theme/useAppTheme";
import { PanelFrameFallback } from "@/views/neighbourhood-map/components/PanelFrameFallback";
import { QUICK_VIEW_PARAM } from "@/core/overlays/quickViewUrl";
import { useAddressSearchParams } from "./useAddressSearchParams";
import type { ListingCard as MlsListingCard } from "@/services/mls/mlsPropertyService";
import type { ListingPin } from "@/components/interactive-map/MapboxNeighborhoodMap";

// Map-first default view. ssr:false keeps the whole map view (ResultsPanel,
// Virtuoso, filters) OFF the critical bundle: SSR-ing it dragged Lantern's
// simulated TTI/LCP to ~7.5s because it models all that JS on a throttled CPU.
// An ssr:false loader never reaches the server HTML, so the server shell
// (PropertiesShellFallback) paints the map pane and the panel frame with the H1.
// The loader keeps the server shell's low static map in the same box, so the
// pane does not flash to a grid and back while the chunk loads.
function MapViewLoading() {
  const { theme } = useAppTheme();
  return <MapPaneFallback mode={theme.mode === "dark" ? "dark" : "light"} />;
}

const NeighbourhoodMapView = dynamic(
  () => import("@/views/neighbourhood-map").then((m) => m.NeighbourhoodMapView),
  { ssr: false, loading: () => <MapViewLoading /> },
);

const FILTER_URL_SYNC_DEBOUNCE_MS = 350;
const URL_PASSTHROUGH_PARAMS = [
  "landing_slug",
  "landing_variant",
  "landing_intent",
  "landing_locale",
  "landing_pin",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  // Search handoff: the AI tab flag and its queued prose keep the URL shareable.
  "ai",
  "aiq",
  // The open quick view (shareable, Back-closable).
  QUICK_VIEW_PARAM,
];

function buildSearchQuery(
  value: TraditionalSearchValue,
  limit: number,
  offset: number,
): string {
  // filtersToSearchParams is the single source of truth for filter -> query
  // params; this wrapper only layers on pagination.
  const sp = filtersToSearchParams(value);
  sp.set("limit", String(limit));
  sp.set("offset", String(offset));
  return sp.toString();
}

type PropertiesPageProps = {
  initialMlsCards: MlsListingCard[];
  initialTotal: number | null;
  mlsPageSize: number;
  /** Server-rendered PanelIntro (the page H1) for the top of the results panel. */
  panelIntro?: ReactNode;
};

function appendPassthroughParams(
  target: URLSearchParams,
  source: URLSearchParams,
): void {
  for (const key of URL_PASSTHROUGH_PARAMS) {
    for (const value of source.getAll(key)) {
      target.append(key, value);
    }
  }
}

function paramsUrl(pathname: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

// Structural equality check: reference equality fails for hydrated objects.
function isDefaultFilters(f: TraditionalSearchValue): boolean {
  return (
    f.regions.length === 0 &&
    f.propertyTypes.length === 0 &&
    f.openHouseOnly === false &&
    !f.presaleOnly &&
    JSON.stringify(f.statuses) === JSON.stringify(DEFAULT_TRADITIONAL.statuses) &&
    f.sort === DEFAULT_TRADITIONAL.sort &&
    f.priceMin == null &&
    f.priceMax == null &&
    f.bedsMin == null &&
    f.bathsMin == null &&
    f.livingAreaMin == null &&
    f.livingAreaMax == null &&
    f.yearBuiltMin == null &&
    f.yearBuiltMax == null &&
    f.lotSizeMin == null &&
    f.lotSizeMax == null &&
    f.parkingMin == null &&
    f.storiesMin == null &&
    (f.keywords?.length ?? 0) === 0 &&
    (f.pills?.length ?? 0) === 0 &&
    !f.address?.trim() &&
    f.mode !== 'rent' &&
    !f.availableBy &&
    !f.petsAllowed &&
    !f.furnishedOnly &&
    !f.inUnitLaundry &&
    !f.leaseTerm
  );
}

const PropertiesPageComponent = ({
  initialMlsCards,
  mlsPageSize,
  panelIntro,
}: PropertiesPageProps) => {
  const { trackEvent } = useAnalytics();
  const syncPreferences = usePreferenceSync();
  const { user } = useSupabaseUser();
  const router = useRouter();
  const pathname = usePathname();

  const [cards, setCards] = useState<MlsListingCard[]>(initialMlsCards);
  // The server-rendered panel frame holds the H1 until the map view mounts,
  // then the live panel takes the same node in the same frame: one H1 in the
  // DOM at any time, and always in the server HTML.
  const [panelMounted, setPanelMounted] = useState(false);
  const handlePanelMounted = useCallback(() => setPanelMounted(true), []);

  // If the SSR fetch returned empty (slow MLS, network blip), refetch on mount
  // so the panel seed and the crawl nav are not empty.
  useEffect(() => {
    if (initialMlsCards.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const rentSeed = featureFlags.rentals && isRentSearchPath(pathname ?? '');
        const res = await fetch(`/api/mls/listings?${rentSeed ? 'mode=rent&' : ''}limit=${mlsPageSize}`);
        const { results } = parseListingsResponse(
          res.ok ? await res.json() : null,
        );
        if (!cancelled) setCards(results);
      } catch {
        // The map view runs its own viewport fetch; the seed stays empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialMlsCards.length, mlsPageSize, pathname]);

  // Not useSearchParams: a map quick view pushState of `?listing=` would
  // suspend this tree back to PropertiesShellFallback and lock the page.
  const searchParams = useAddressSearchParams();
  // Search handoff intake, before the map view mounts: queue `aiq` prose for
  // the AI tab once, and send a bare legacy `?q=` through the site resolver
  // (MLS® number, place or prose) instead of a literal street filter.
  useEffect(() => {
    const inbound = new URLSearchParams(window.location.search);
    const aiq = inbound.get("aiq");
    if (aiq) queueAiSeedFromUrl(aiq);
    const q = inbound.get("q");
    if (!q || inbound.has("address") || aiq) return;
    let cancelled = false;
    void resolveSearchQuery(q).then((destination) => {
      if (!cancelled && destination.type !== "address") replaceLocation(destination.href);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [traditionalOpen, setTraditionalOpen] = useState(false);
  const [filters, setFilters] =
    useState<TraditionalSearchValue>(DEFAULT_TRADITIONAL);
  const filterChangeCount = useRef(0);
  const urlSyncReady = useRef(false);
  const urlSyncDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Hydrate filter state on mount:
  // 1. URL params take highest priority (landing pages / filter modal)
  // 2. Authenticated: fetch filter profile from DB (single source of truth)
  //    and merge over localStorage so last-known DB state wins
  // 3. Anonymous: restore from localStorage only
  useEffect(() => {
    // The page is the mode: /rent opens the rental search, /properties the sale search.
    const rentPage = featureFlags.rentals && isRentSearchPath(pathname);
    const query = new URLSearchParams(window.location.search);
    query.delete('mode');
    const urlFilters = filtersFromQuery(query);
    if (urlFilters) {
      setFilters(rentPage ? { ...urlFilters, mode: 'rent' } : urlFilters);
      setTraditionalOpen(true);
      filterChangeCount.current = 1;
      return;
    }
    const stored = getFilterState(rentPage ? 'rent' : 'buy');
    const localTs = stored?.updatedAt ?? 0;
    if (stored) {
      setFilters(rentPage ? { ...stored.filters, mode: 'rent' } : stored.filters);
      if (stored.open) setTraditionalOpen(true);
    } else if (rentPage) {
      setFilters({ ...DEFAULT_TRADITIONAL, mode: 'rent' });
    }
    if (!user) return;
    // Authenticated: fetch DB profile and apply it ONLY when it's a genuinely
    // newer manual edit than the local blob (last-write-wins). Without this, a
    // fresh same-device edit: including one made on the map, which writes the
    // same local blob: gets clobbered by a stale server profile on every reload.
    fetch("/api/dashboard/filter-profile")
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (
          profile: {
            regions?: string[];
            propertyTypes?: string[];
            bedsMin?: number | null;
            bathsMin?: number | null;
            priceMin?: number | null;
            priceMax?: number | null;
            livingAreaMin?: number | null;
            parkingMin?: number | null;
            openHouseOnly?: boolean;
            keywords?: string[];
            sort?: string;
            filters?: Partial<TraditionalSearchValue> | null;
            lastManualUpdate?: string | null;
          } | null,
        ) => {
          if (!profile) return;
          // The saved profile is the buy search; a Rent search never takes it.
          // Last-write-wins: keep local when the server's last manual edit is not
          // newer than the local write. With no local blob (first visit on this
          // device) the server profile/seed applies.
          const serverTs = profile.lastManualUpdate ? new Date(profile.lastManualUpdate).getTime() : 0;
          if (stored && serverTs <= localTs) return;
          // Lossless blob wins when present: it carries every field, so the
          // saved preference restores exactly across device/login. Merge over
          // prev so a same-device in-progress edit isn't thrown away.
          if (profile.filters && typeof profile.filters === "object") {
            // Normalized: the blob is whatever a past client version persisted,
            // so a field it never wrote (or wrote as null) must not land as a
            // non-array on a filter every consumer calls `.length` on.
            setFilters((prev) => (prev.mode === 'rent' ? prev : normalizeFilterValue({ ...prev, ...profile.filters })));
            return;
          }
          // Legacy typed-column merge: profiles saved before the blob existed.
          setFilters((prev) => prev.mode === 'rent' ? prev : ({
            ...prev,
            ...(profile.regions?.length ? { regions: profile.regions } : {}),
            ...(profile.propertyTypes?.length
              ? { propertyTypes: profile.propertyTypes }
              : {}),
            ...(profile.bedsMin != null ? { bedsMin: profile.bedsMin } : {}),
            ...(profile.bathsMin != null ? { bathsMin: profile.bathsMin } : {}),
            ...(profile.priceMin != null ? { priceMin: profile.priceMin } : {}),
            ...(profile.priceMax != null ? { priceMax: profile.priceMax } : {}),
            ...(profile.livingAreaMin != null
              ? { livingAreaMin: profile.livingAreaMin }
              : {}),
            ...(profile.parkingMin != null
              ? { parkingMin: profile.parkingMin }
              : {}),
            ...(profile.openHouseOnly != null
              ? { openHouseOnly: profile.openHouseOnly }
              : {}),
            ...(profile.keywords?.length ? { keywords: profile.keywords } : {}),
            ...(profile.sort
              ? { sort: profile.sort as TraditionalSearchValue["sort"] }
              : {}),
          }));
        },
      )
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Persist to localStorage on change and sync to DB (debounced 1.2s).
  // Skipped when state is default + closed (clean slate, no noise).
  useEffect(() => {
    if (isDefaultFilters(filters) && !traditionalOpen) return;
    setFilterState({ filters, open: traditionalOpen });
    // The DB filter profile is the buy search (saved searches, alerts, AI
    // context); rental filters stay in their own local memory.
    if (filters.mode === "rent") return;
    syncPreferences({
      regions: filters.regions,
      propertyTypes: filters.propertyTypes,
      bedsMin: filters.bedsMin ?? null,
      bathsMin: filters.bathsMin ?? null,
      priceMin: filters.priceMin ?? null,
      priceMax: filters.priceMax ?? null,
      livingAreaMin: filters.livingAreaMin ?? null,
      parkingMin: filters.parkingMin ?? null,
      openHouseOnly: filters.openHouseOnly,
      keywords: filters.keywords,
      sort: filters.sort,
      // Full lossless blob: carries every field, including the ones with no
      // typed column (address, pills, statuses, yearBuilt*, lotSize*, etc.).
      filters: filters as unknown as Record<string, unknown>,
    });
  }, [filters, traditionalOpen]); // syncPreferences is stable (useCallback)

  // Size the shell to the exact space below the navbar by measuring, before
  // paint, instead of `calc(100dvh - var(--nav-height))`. The nav var is set
  // late by JS, and a CSS default that disagreed with the real header height
  // caused a load-time resize = ~0.18 CLS.
  const mapShellRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = mapShellRef.current;
    if (!el) return;
    const fit = () => {
      const top = el.getBoundingClientRect().top;
      el.style.height = `${Math.max(360, window.innerHeight - top)}px`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  const requestSeq = useRef(0);
  const landingPin = searchParams.get("landing_pin");
  // Deep-link a single home onto the map (from the AI chat "View on map"): a
  // highlighted marker that self-centers at zoom 17, independent of the pins
  // loaded for the current viewport. Requires coordinates; the rest is display
  // sugar for the marker card.
  const pinLat = Number(searchParams.get("pin_lat"));
  const pinLng = Number(searchParams.get("pin_lng"));
  const pinNum = (v: string | null) => (v && Number.isFinite(Number(v)) ? Number(v) : null);
  const chatListingPin: ListingPin | undefined =
    Number.isFinite(pinLat) && Number.isFinite(pinLng) && pinLat !== 0 && pinLng !== 0
      ? {
          latitude: pinLat,
          longitude: pinLng,
          title: searchParams.get("pin_title") ?? "",
          price: pinNum(searchParams.get("pin_price")),
          beds: pinNum(searchParams.get("pin_beds")),
          baths: pinNum(searchParams.get("pin_baths")),
          sqft: pinNum(searchParams.get("pin_sqft")),
          href: searchParams.get("pin_href") ?? undefined,
        }
      : undefined;
  const visible = useMemo(() => {
    if (!landingPin) return cards;
    const idx = cards.findIndex((c) => c.listingKey === landingPin);
    if (idx <= 0) return cards;
    return [cards[idx], ...cards.slice(0, idx), ...cards.slice(idx + 1)];
  }, [cards, landingPin]);

  // Write nav context so the detail page sticky bar can show prev/next arrows
  useEffect(() => {
    writeNavContext({
      slugs: visible.map((c) => c.listingKey),
      source: "search",
      label: "Search Results",
      backHref: "/properties",
    });
  }, [visible]);

  // Keep the shareable /properties URL in sync when users refine filters after
  // arriving from a landing page or the Home Finder modal.
  useEffect(() => {
    if (!urlSyncReady.current) {
      urlSyncReady.current = true;
      return;
    }
    if (!traditionalOpen && isDefaultFilters(filters)) return;
    if (urlSyncDebounce.current) clearTimeout(urlSyncDebounce.current);

    urlSyncDebounce.current = setTimeout(() => {
      // The live query, not the render's: a quick view may have pushed
      // `listing` since this effect captured searchParams.
      const currentParams = new URLSearchParams(window.location.search);
      const nextParams = isDefaultFilters(filters)
        ? new URLSearchParams()
        : filtersToSearchParams(filters);

      appendPassthroughParams(nextParams, currentParams);
      // The rental page's path already says Rent; its URL never carries the flag.
      if (isRentSearchPath(pathname)) nextParams.delete('mode');

      const currentUrl = paramsUrl(pathname, currentParams);
      const nextUrl = paramsUrl(pathname, nextParams);
      if (nextUrl !== currentUrl) {
        router.replace(nextUrl, { scroll: false });
      }
    }, FILTER_URL_SYNC_DEBOUNCE_MS);

    return () => {
      if (urlSyncDebounce.current) clearTimeout(urlSyncDebounce.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, traditionalOpen, pathname, router]);

  // Debounced re-fetch of the seed cards when hydrated filters change, with
  // the search analytics. Skips the first evaluation at defaults.
  useEffect(() => {
    if (!traditionalOpen) return;
    if (isDefaultFilters(filters) && filterChangeCount.current === 0) return;
    filterChangeCount.current += 1;
    const seq = ++requestSeq.current;
    const url = `/api/mls/listings?${buildSearchQuery(filters, mlsPageSize, 0)}`;
    const fetchStart = performance.now();
    let fetchOk = true;
    fetch(url)
      .then((res) => {
        fetchOk = res.ok;
        return res.ok ? res.json() : null;
      })
      .then((raw: unknown) => {
        if (seq !== requestSeq.current) return;
        const { count, results } = parseListingsResponse(raw);
        setCards(results);
        const durationMs = Math.round(performance.now() - fetchStart);
        trackEvent({
          eventName: "mls_traditional_search_result",
          eventType: "search",
          category: "search",
          metadata: {
            result_count: count ?? results.length,
            query_duration_ms: durationMs,
            ok: fetchOk,
            listing_type: filters.mode === "rent" ? "lease" : "sale",
            ...searchGaParams(filters),
          },
        });
        reltaTrack("search.run", {
          result_count: count ?? results.length,
          query_duration_ms: durationMs,
          search_text: filters.address || undefined,
          regions: filters.regions,
          filters: {
            keywords: filters.keywords,
            pills: filters.pills,
            propertyTypes: filters.propertyTypes,
            priceMin: filters.priceMin,
            priceMax: filters.priceMax,
            bedsMin: filters.bedsMin,
            bathsMin: filters.bathsMin,
            statuses: filters.statuses,
          },
        });
        if ((count ?? results.length) === 0) {
          trackEvent({
            eventName: "mls_search_zero_results",
            eventType: "search",
            category: "search",
            metadata: zeroResultsGaParams(filters),
          });
          reltaTrack("search.zero_results", {
            search_text: filters.address || undefined,
            regions: filters.regions,
            filters: {
              keywords: filters.keywords,
              pills: filters.pills,
              propertyTypes: filters.propertyTypes,
              priceMin: filters.priceMin,
              priceMax: filters.priceMax,
              bedsMin: filters.bedsMin,
              bathsMin: filters.bathsMin,
              statuses: filters.statuses,
            },
          });
        }
      })
      .catch(() => {
        // The seed keeps its last cards; the map view owns the visible results.
      });
    trackEvent({
      eventName: "mls_traditional_search_refined",
      eventType: "search",
      category: "search",
      metadata: {
        priceMin: filters.priceMin,
        priceMax: filters.priceMax,
        bedsMin: filters.bedsMin,
        bathsMin: filters.bathsMin,
        propertyTypes: filters.propertyTypes,
        livingAreaMin: filters.livingAreaMin,
        livingAreaMax: filters.livingAreaMax,
        yearBuiltMin: filters.yearBuiltMin,
        yearBuiltMax: filters.yearBuiltMax,
        lotSizeMin: filters.lotSizeMin,
        lotSizeMax: filters.lotSizeMax,
        parkingMin: filters.parkingMin,
        storiesMin: filters.storiesMin,
        openHouseOnly: filters.openHouseOnly,
        statuses: filters.statuses,
        sort: filters.sort,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, mlsPageSize]);

  // The list is seeded with the server-fetched cards so the LCP element is
  // text, not the GL canvas.
  return (
      <Box
        ref={mapShellRef}
        sx={{
          position: "relative",
          height: "calc(100dvh - var(--nav-height, 76px))",
          minHeight: 0,
        }}
      >
        <NeighbourhoodMapView
          edgeToEdge
          deferMapMount
          keepMobileCompareTray
          showSaveSearchAction
          initialListings={visible}
          listingPin={chatListingPin}
          panelIntro={panelMounted ? panelIntro : undefined}
          onPanelMounted={handlePanelMounted}
        />
        {panelMounted ? null : <PanelFrameFallback intro={panelIntro} />}
        {/* Crawlable links to every seeded listing. The map's result cards open
            a slide-over (no <a href>), so this keeps the indexed /properties hub
            linking out to detail pages for crawl discovery. Visually hidden,
            real anchors, not display:none cloaking. */}
        <Box
          component="nav"
          aria-hidden="true"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        >
          {visible.map((c) => (
            <Link
              key={c.listingKey}
              href={`/properties/${c.listingSlug ?? c.listingKey}`}
              // The nav is aria-hidden (crawler-only); focusable elements
              // inside an aria-hidden region are a WCAG violation, so keep
              // these out of the tab order too.
              tabIndex={-1}
            >
              {[c.streetNumber, c.streetName, c.city].filter(Boolean).join(" ") || c.listingKey}
            </Link>
          ))}
        </Box>
      </Box>
  );
};

const MemoizedPage = memo(PropertiesPageComponent);

const PropertiesPage = (props: PropertiesPageProps) => (
  <SuggestToClientProvider>
    <MemoizedPage {...props} />
  </SuggestToClientProvider>
);

PropertiesPage.displayName = "PropertiesPage";

export default PropertiesPage;
